import { Component, computed, effect, inject, input, model, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { USER_ROLE_TRANSLATION_KEYS, UserRole } from '@navfleet/client/authentication/domain/models';
import { License } from '@navfleet/client/license/domain/models';
import { Permission } from '@navfleet/client/permission/domain/models';
import { IconContainer } from '@navfleet/client/shared/components/icon/ui';
import { LogoComponent } from '@navfleet/client/shared/components/logo';
import { Fleet } from '@navfleet/client/shared/models/common';
import { Vessel } from '@navfleet/client/shared/models/vessel';
import { FullscreenTriggerDirective } from '@navfleet/client/shared/ui/fullscreen';
import { Breadcrumb, LayoutNotification } from '@navfleet/client/shell/domain/models';
import { Tenant } from '@navfleet/client/tenant/domain/models';
import { User } from '@navfleet/client/user/domain/models';
import { TranslationService } from '@navfleet/shared/core/translation';
import { TranslatePipe } from '@ngx-translate/core';
import { MenuItem } from '@openng/optimus-ui/api';
import { AutoComplete, AutoCompleteCompleteEvent, AutoCompleteSelectEvent } from '@openng/optimus-ui/autocomplete';
import { Button } from '@openng/optimus-ui/button';
import { Menu } from '@openng/optimus-ui/menu';
import { MultiSelect } from '@openng/optimus-ui/multiselect';
import { OverlayBadge } from '@openng/optimus-ui/overlaybadge';
import { Popover } from '@openng/optimus-ui/popover';
import { Ripple } from '@openng/optimus-ui/ripple';
import { sortBy } from 'lodash';
import { TopMenuBreadcrumbsComponent } from './breadcrumbs/top-menu-breadcrumbs.component';
import { TopMenuClockComponent } from './clock/top-menu-clock.component';
import { TopMenuNotificationsComponent } from './notifications/top-menu-notifications.component';

interface TopMenuVessel extends Vessel {
    idString: string;
}

@Component({
    imports: [
        LogoComponent,
        TopMenuBreadcrumbsComponent,
        AutoComplete,
        FormsModule,
        TranslatePipe,
        Button,
        Menu,
        IconContainer,
        MultiSelect,
        RouterLink,
        TopMenuNotificationsComponent,
        TopMenuClockComponent,
        Popover,
        OverlayBadge,
        FullscreenTriggerDirective,
        RouterLinkActive,
        Ripple,
    ],
    selector: 'fl-top-menu',
    templateUrl: './top-menu.component.html',
    styleUrls: ['./top-menu.component.scss'],
})
export class TopMenuComponent {
    private readonly translationService = inject(TranslationService);

    public readonly USER_ROLE_TRANSLATION_KEYS = USER_ROLE_TRANSLATION_KEYS;
    public readonly license = License;
    public readonly permission = Permission;

    public readonly notifications = input<LayoutNotification[]>([]);
    public readonly filteredOutNotificationCount = input<{ [x: string]: number } | null | undefined>(null);
    public readonly breadcrumbs = input<Breadcrumb[]>([]);
    public readonly vessels = input<Vessel[]>([]);
    public readonly isLoadingVessels = input(false);
    public readonly fleets = input<Fleet[]>([]);
    public readonly selectedFleetIds = model<number[]>([]);
    public readonly timezone = input<string>();
    public readonly user = input<User>();
    public readonly userRole = input<UserRole | null | undefined>();
    public readonly userCustomRoleName = input<string>();
    public readonly licenses = input<License[]>([]);
    public readonly permissions = input<Permission[]>();
    public readonly tenant = input<Tenant>();
    public readonly userCompanyName = input<string | null>();
    public readonly selectedVesselId = model<string | undefined>(undefined);
    public readonly logoutClick = output();
    public readonly consentClick = output();
    public readonly notificationsRead = output();

    public readonly filteredVessels = signal<TopMenuVessel[]>([]);
    public readonly autoCompleteComponent = viewChild<AutoComplete>('autoComplete');
    public readonly sortedFleets = computed(() => sortBy(this.fleets(), (fleet) => fleet.name.toLowerCase()));

    public readonly userMenuItems: MenuItem[] = [
        {
            label: this.translationService.instant('TOP_MENU.CONSENT'),
            command: () => this.consentClick.emit(),
            automationId: 'topmenu-consent',
            icon: 'cookie',
        },
        {
            label: this.translationService.instant('TOP_MENU.SIGN_OUT'),
            command: () => this.logoutClick.emit(),
            automationId: 'topmenu-logout',
            icon: 'logout',
        },
    ];

    public readonly userInfo = computed(() => {
        const tenant = this.tenant();
        const vessels = this.vessels();
        const userCompanyName = this.userCompanyName();

        const userInfo: { label: string; value: string }[] = [];

        if (userCompanyName) {
            userInfo.push({ label: this.translationService.instant('TOPMENU.COMPANY'), value: userCompanyName });
        }
        if (tenant) {
            userInfo.push({ label: this.translationService.instant('COMMON.PORTAL'), value: tenant.name });
        }
        if (vessels) {
            userInfo.push({
                label: this.translationService.instant('COMMON.VESSELS'),
                value: this.translationService.instant('TOPMENU.VESSEL_COUNT', { count: vessels.length }),
            });
        }

        return userInfo;
    });

    public readonly notificationsPinned = signal<boolean>(false);
    public readonly notificationsCount = computed(
        () => this.notifications()?.filter((notification) => !notification.seen).length ?? 0
    );

    constructor() {
        effect(() => {
            if (this.autoCompleteComponent() && this.selectedVesselId()) {
                setTimeout(() => this.resetAutoCompleteValue());
            }
        });
    }

    public onVesselSelect(event: AutoCompleteSelectEvent) {
        const vessel: Vessel = event.value;
        this.selectedVesselId.set(vessel.id.toString());
        this.resetAutoCompleteValue();
    }

    public onAutoCompleteShow() {
        const el = this.autoCompleteComponent()?.el?.nativeElement as HTMLElement;
        setTimeout(() => el.querySelector<HTMLElement>('.p-autocomplete-option-selected')?.scrollIntoView());
    }

    public filterVesselSuggestions(e: AutoCompleteCompleteEvent) {
        const vessels = this.vessels() ?? [];
        this.filteredVessels.set(
            sortBy(
                e.query
                    ? vessels.filter((vessel) => vessel.name.toLowerCase().includes(e.query.toString().toLowerCase()))
                    : vessels,
                (vessel) => vessel.name
            ).map<TopMenuVessel>((vessel) => ({ ...vessel, idString: vessel.id.toString() }))
        );
    }

    public resetAutoCompleteValue() {
        (this.autoCompleteComponent()?.inputEL?.nativeElement as HTMLInputElement).value = '';
    }
}
